/**
 * A Review object includes all of the details related to the review of an entity.
 * An entity is a specific resource to review, for example, a store product.
 * You can manage existing reviews, create new reviews, and retrieve reviews.
 * Read more about [Adding and Setting Up Wix Reviews](https://support.wix.com/en/article/wix-stores-adding-and-setting-up-wix-reviews).
 */
export interface Review {
  /**
   * Review namespace.
   *
   * Supported values:
   * + Wix Stores: `stores`
   *
   * Currently, only Wix Stores is fully integrated with the Wix Reviews app.
   */
  namespace?: string;
  /**
   * ID of the entity to review. For example, a Wix Stores product ID.
   * @maxLength 36
   */
  entityId?: string;
  /**
   * Review ID.
   * @format GUID
   * @readonly
   */
  id?: string;
  /** Review content. */
  content?: ReviewContent;
  /**
   * Date and time the review was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the review was updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /** Author of the review. */
  author?: Author;
  /**
   * Reply to the review.
   * @readonly
   */
  reply?: Reply;
  /**
   * Number of site visitors who found the review helpful.
   * @readonly
   */
  foundHelpful?: number;
  /**
   * Number of site visitors who found the review unhelpful.
   * @readonly
   */
  foundUnhelpful?: number;
  /**
   * Helpfulness score.
   *
   * Calculated by subtracting `foundUnhelpful` from `foundHelpful`.
   * @readonly
   */
  helpfulness?: number;
  /**
   * Moderation status of the review.
   * @readonly
   */
  moderation?: Moderation;
  /**
   * Revision number, which increments by 1 each time the review is updated.
   * To prevent conflicting changes,
   * the current `revision` must be passed when updating the review.
   * Ignored when creating a review.
   * @readonly
   */
  revision?: string | null;
  /**
   * Whether the review has been verified.
   * @readonly
   */
  verified?: boolean;
  /**
   * Origin of a review.
   * @readonly
   */
  origin?: Origin;
  /**
   * Date and time when the review was written.
   * This should match `createdDate`, except for reviews imported from another system.
   */
  reviewDate?: Date | null;
  /**
   * Platform-calculated score of review's relevance to the item.
   * Used to sort reviews by relevance.
   * @readonly
   */
  relevanceScore?: number | null;
  /**
   * Number of times a review has been published.
   * @readonly
   */
  publishCount?: number;
}

/** The review content. */
export interface ReviewContent {
  /**
   * Review title.
   * @maxLength 3000
   */
  title?: string | null;
  /**
   * Review body.
   *
   * Max: 3,000 characters
   * @maxLength 3000
   */
  body?: string | null;
  /**
   * List of media items associated with the review.
   *
   * Max: 10 items
   * @maxSize 10
   */
  media?: Media[];
  /**
   * Rating of the review.
   *
   * Min: `1` <br />
   * Max: `5`
   */
  rating?: number;
}

/** Media item associated with the review. */
export interface Media extends MediaMediaOneOf {
  /** Image media item. */
  image?: Image;
  /** Video media item. */
  video?: VideoV2;
}

/** @oneof */
export interface MediaMediaOneOf {
  /** Image media item. */
  image?: Image;
  /** Video media item. */
  video?: VideoV2;
}

export interface Image {
  /** WixMedia image ID. */
  id?: string;
  /** Image URL. */
  url?: string;
  /**
   * Original image height.
   * @readonly
   */
  height?: number;
  /**
   * Original image width.
   * @readonly
   */
  width?: number;
  /**
   * Alternative text is a short phrase that describes what the image depicts.
   *
   * The alternative text is used:
   * + If the browser can't display the image.
   * + If the visitor is utilizing a screen reader.
   * + By search engines to understand what images are on a site.
   * @readonly
   */
  altText?: string | null;
  /**
   * Image filename.
   * @readonly
   */
  filename?: string | null;
}

export interface VideoV2 {
  /** WixMedia video ID. */
  id?: string;
  /**
   * Available resolutions for the video, starting with the optimal resolution.
   * @readonly
   */
  resolutions?: VideoResolution[];
  /** Video URL. */
  url?: string;
}

export interface VideoResolution {
  /**
   * Video URL.
   * @readonly
   */
  url?: string;
  /**
   * Video height.
   * @readonly
   */
  height?: number;
  /**
   * Video width.
   * @readonly
   */
  width?: number;
  /**
   * Video thumbnail.
   * @readonly
   */
  poster?: Image;
  /**
   * Video format for example, mp4, hls.
   * @readonly
   */
  format?: string;
}

/** Review author. */
export interface Author {
  /**
   * Contact ID of the author.
   * @format GUID
   * @immutable
   */
  contactId?: string | null;
  /**
   * Display name of the author.
   * @maxLength 100
   */
  authorName?: string | null;
}

/** Supported types of review author. */
export enum AuthorType {
  UNKNOWN = 'UNKNOWN',
  MEMBER = 'MEMBER',
  VISITOR = 'VISITOR',
}

/** @enumType */
export type AuthorTypeWithLiterals =
  | AuthorType
  | 'UNKNOWN'
  | 'MEMBER'
  | 'VISITOR';

/** Reply to the review. */
export interface Reply {
  /**
   * Reply content.
   * @maxLength 1000
   */
  message?: string;
  /**
   * Date and time the reply was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the reply was updated.
   * @readonly
   */
  updatedDate?: Date | null;
}

/** Moderation status of the review. */
export interface Moderation {
  moderationStatus?: ModerationModerationStatusWithLiterals;
  /**
   * Indicates whether a moderator manually changed the `moderationStatus` of the review.
   *
   * If the `moderationStatus` changed automatically because the review
   * passed the moderation rules or if moderation is turned off, this field is set to `false`.
   * @readonly
   */
  manuallyChanged?: boolean;
}

/** Supported states of the review. */
export enum ModerationModerationStatus {
  /** Unknown moderation status. */
  UNKNOWN = 'UNKNOWN',
  /** The review is approved and published. */
  APPROVED = 'APPROVED',
  /** The review is pending moderation. Moderation can be applied in the Wix user's [dashboard](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Freviews/pending). */
  IN_MODERATION = 'IN_MODERATION',
  /** The review is rejected and not visible on the site. */
  REJECTED = 'REJECTED',
  /** Initial status of the review before the moderation process. */
  SUBMITTED = 'SUBMITTED',
}

/** @enumType */
export type ModerationModerationStatusWithLiterals =
  | ModerationModerationStatus
  | 'UNKNOWN'
  | 'APPROVED'
  | 'IN_MODERATION'
  | 'REJECTED'
  | 'SUBMITTED';

export interface Origin {
  /** Origin type. */
  type?: OriginTypeWithLiterals;
  /**
   * The app ID of the origin for the review.
   * Only available when the `origin.type` is `"APP"`.
   */
  appId?: string | null;
}

/** Supported types of review origin. */
export enum OriginType {
  /** Unknown origin type. */
  UNKNOWN = 'UNKNOWN',
  /** Review was created directly by members or site visitors. */
  ORGANIC = 'ORGANIC',
  /** Review was created through an app. Includes reviews created through an app even if the author is a member or site visitor. */
  APP = 'APP',
}

/** @enumType */
export type OriginTypeWithLiterals = OriginType | 'UNKNOWN' | 'ORGANIC' | 'APP';

export interface Source extends SourceSourceTypeOptionsOneOf {
  /**
   * Only available if `source.type` is `"REVIEW_REQUEST"`.
   * See the [Review Requests API](https://dev.wix.com/docs/rest/crm/community/reviews/review-requests/introduction) for more information.
   */
  reviewRequestOptions?: ReviewRequestOptions;
  /** Type of review source. */
  type?: SourceTypeWithLiterals;
}

/** @oneof */
export interface SourceSourceTypeOptionsOneOf {
  /**
   * Only available if `source.type` is `"REVIEW_REQUEST"`.
   * See the [Review Requests API](https://dev.wix.com/docs/rest/crm/community/reviews/review-requests/introduction) for more information.
   */
  reviewRequestOptions?: ReviewRequestOptions;
}

export enum SourceType {
  /**
   * Review was created from a review request.
   * See the [Review Requests API](https://dev.wix.com/docs/rest/crm/community/reviews/review-requests/introduction) for more information.
   */
  REVIEW_REQUEST = 'REVIEW_REQUEST',
}

/** @enumType */
export type SourceTypeWithLiterals = SourceType | 'REVIEW_REQUEST';

export interface ReviewRequestOptions {
  /** Review request ID. */
  id?: string | null;
}

/** Triggered when a review's moderation status is set to `APPROVED`. */
export interface ReviewPublished {
  /** Review entity */
  review?: Review;
}

/** Triggered when a review's moderation status is changed or a review is created. */
export interface ModerationStatusChanged {
  /** Review entity. */
  review?: Review;
  /**
   * The previous moderation status of the review.
   * - `APPROVED`: The review is approved and published.
   * - `IN_MODERATION`: The review is pending moderation. Moderation can be applied in the Wix user's [dashboard](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Freviews/pending).
   * - `REJECTED`: The review is rejected and not visible in the site.
   * - `SUBMITTED`: Initial status of the review before the moderation process.
   */
  previousModerationStatus?: ModerationModerationStatusWithLiterals;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface GetReviewRequest {
  /**
   * Review ID.
   * @format GUID
   */
  reviewId: string;
  /**
   * Whether to return unpublished reviews.
   *
   * Default: `false`
   */
  returnPrivateReviews?: boolean;
}

export interface GetReviewResponse {
  /** Review. */
  review?: Review;
}

export interface CreateReviewAndContactRequest {
  /**
   * Namespace where the review is created.
   * @maxLength 36
   */
  namespace?: string;
  /**
   * ID of the entity to review.
   * @maxLength 36
   */
  entityId?: string;
  /** Name of the review author. Displayed as part of the review. Required for 3rd-party apps. */
  name?: string | null;
  /**
   * Email address of the review author. Required for 3rd-party apps.
   * @format EMAIL
   */
  email?: string | null;
  /** Review content. */
  content?: ReviewContent;
}

export interface CreateReviewResponse {
  /** Created review. */
  review?: Review;
}

export interface CreateReviewRequest {
  /** Review data. */
  review: Review;
}

export interface BulkCreateReviewRequest {
  /**
   * Reviews to create.
   * @minSize 1
   * @maxSize 100
   */
  reviews: Review[];
  /** Whether to return the created entities. */
  returnEntity?: boolean;
}

export interface BulkCreateReviewResponse {
  /**
   * The created reviews.
   * Only returned if the `returnEntity` field is value is `true`.
   */
  results?: BulkReviewResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkReviewResult {
  /** ItemMetadata */
  itemMetadata?: ItemMetadata;
  /** New Reviews */
  review?: Review;
}

export interface ItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface UpdateReviewContentRequest {
  /**
   * Review ID.
   * @format GUID
   */
  reviewId?: string;
  /** Review content. */
  content?: ReviewContent;
  /**
   * Fieldmask for update.
   * Supported fields: content.title, content.body, content.media, content.rating
   */
  fieldMask?: string[];
}

export interface UpdateReviewContentResponse {
  /** Updated review data. */
  review?: Review;
}

export interface UpdateReviewRequest {
  /** Review to update. */
  review?: Review;
}

export interface UpdateReviewResponse {
  /** Updated review data. */
  review?: Review;
}

export interface DeleteReviewRequest {
  /**
   * Review ID.
   * @format GUID
   */
  reviewId: string;
}

export interface DeleteReviewResponse {
  /** Review entity. */
  review?: Review;
}

export interface BulkDeleteReviewsRequest {
  /** Query options. See [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language) for more details. */
  filter?: Record<string, any> | null;
}

export interface BulkDeleteReviewsResponse {
  /** Bulk job ID. */
  jobId?: string;
}

export interface QueryReviewsRequest {
  /** Review query. */
  query?: QueryV2;
  /**
   * Whether to return private (unpublished) reviews.
   *
   * Default: `false`
   */
  returnPrivateReviews?: boolean;
}

export interface QueryV2 {
  /** Filter object. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more information. */
  filter?: Record<string, any> | null;
  /** Sorting options. */
  sort?: Sorting[];
  /** Cursor paging options. */
  cursorPaging?: CursorPaging;
}

/** Sort options. */
export interface Sorting {
  /** Name of the field to sort by. */
  fieldName?: string;
  /**
   * Sort order.
   *
   * Default: `ASC`.
   */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  /** Sort by ascending order. */
  ASC = 'ASC',
  /** Sort by descending order. */
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * The number of items to load.
   * Cursor token returned in the query response. To be used on the next query request, but not the first query request.
   *
   * Max: `100` <br />
   * Default: `50`
   * @max 100
   */
  limit?: number | null;
  /** Cursor returned in last query response. Shouldn't be provided on first page request. */
  cursor?: string | null;
}

export interface QueryReviewsResponse {
  /** Paging metadata. */
  metadata?: PagingMetadataV2;
  /** List of returned reviews. */
  reviews?: Review[];
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag isn't set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
}

export interface Cursors {
  /** Cursor pointing to next result page. */
  next?: string | null;
  /** Cursor pointing to previous result page. */
  prev?: string | null;
}

export interface SetReplyRequest {
  /**
   * Review ID.
   * @format GUID
   */
  reviewId: string;
  /**
   * Response to review author.
   * @maxLength 10000
   */
  message: string;
}

export interface SetReplyResponse {
  /** Updated review. */
  review?: Review;
}

export interface RemoveReplyRequest {
  /**
   * ID of review to remove the reply for.
   * @format GUID
   */
  reviewId: string;
}

export interface RemoveReplyResponse {
  /** Updated review. */
  review?: Review;
}

export interface UpdateModerationStatusRequest {
  /**
   * ID of the review to moderate.
   * @format GUID
   */
  reviewId: string;
  /** Moderation status. */
  status?: UpdateModerationStatusRequestModerationStatusWithLiterals;
}

export enum UpdateModerationStatusRequestModerationStatus {
  /** Unknown moderation status. This value isn't used. */
  UNKNOWN_MODERATION_STATUS = 'UNKNOWN_MODERATION_STATUS',
  /** The review has been approved and published, and site visitors can see it. */
  APPROVED = 'APPROVED',
  /** The review is rejected and site visitors can't see it. */
  REJECTED = 'REJECTED',
}

/** @enumType */
export type UpdateModerationStatusRequestModerationStatusWithLiterals =
  | UpdateModerationStatusRequestModerationStatus
  | 'UNKNOWN_MODERATION_STATUS'
  | 'APPROVED'
  | 'REJECTED';

export interface UpdateModerationStatusResponse {
  /** The review after moderation. */
  review?: Review;
}

export interface BulkUpdateModerationStatusRequest {
  /** Reviews to moderate. */
  filter?: Record<string, any> | null;
  /** Moderation status. */
  status?: ModerationStatusWithLiterals;
}

export enum ModerationStatus {
  /** Unknown moderation status. This value isn't used. */
  UNKNOWN = 'UNKNOWN',
  /** The review has been approved and published, and site visitors can see it. */
  APPROVED = 'APPROVED',
  /** The review is rejected and site visitors can't see it. */
  REJECTED = 'REJECTED',
}

/** @enumType */
export type ModerationStatusWithLiterals =
  | ModerationStatus
  | 'UNKNOWN'
  | 'APPROVED'
  | 'REJECTED';

export interface BulkUpdateModerationStatusResponse {
  /** Job ID. */
  jobId?: string;
}

export interface CountReviewsRequest {
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   *
   * See [Query Reviews](https://dev.wix.com/docs/rest/crm/community/reviews/reviews/query-reviews) for a list of supported filters.
   */
  filter?: Record<string, any> | null;
  /**
   * Whether to include unpublished reviews in the count.
   *
   * Default: `false`
   */
  includePrivateReviews?: boolean;
}

export interface CountReviewsResponse {
  /** Number of reviews. */
  count?: number;
}

/** @docsIgnore */
export type CountReviewsApplicationErrors = {
  code?: 'TOO_MANY_TO_COUNT';
  description?: string;
  data?: Record<string, any>;
};
